import React, { ChangeEvent, useCallback, useMemo } from 'react';
import { CellContext } from '@tanstack/react-table';
import { Typography } from '@mui/material';
import { OptionalInherited } from '@nexspec/warehouse-shared-types/src/titles/TitleMetadata/Inherited';
import { titleEditorStoreSelector, useTitleEditorStore } from '@warehouse/title/domain';
import { DropdownV2, ReadOnlyTypography } from '@warehouse/shared/ui';
import { isInherited } from '@warehouse/title/core';
import { CustomColumnDef } from '../../../../../components/library/SimpleTable/customColumnDef';
import TitleEditableField from '../../../../../components/titles/TitleEditableField';
import InheritanceOnValueChangeSimpleTableWrapper from '../../../../../components/library/SimpleTable/Wrappers/InheritanceOnValueChangeSimpleTableWrapper';
import { TableTextInput } from '../style';
import { CellWrapper, ScrollableReadOnlyWrapper } from '../../../../../components/library/SimpleTable/style';
import { UseTitleAutoSaveOutput } from '../../../hooks/useTitleAutoSave/useTitleAutoSave';
import { InheritedWrapper } from '../../../../../components/library/SimpleTable/Wrappers/utils';

export type CustomMetadataColumn = {
	name: string;
} & (
	| {
			value: string;
			type: 'string';
			autoSave: UseTitleAutoSaveOutput<OptionalInherited<string>>;
	  }
	| {
			value: string;
			type: 'boolean';
			autoSave: UseTitleAutoSaveOutput<OptionalInherited<boolean>>;
	  }
);

interface CustomMetadataBaseProps {
	data: CustomMetadataColumn[];
}

function KeyCell(info: CellContext<CustomMetadataColumn, string>) {
	const { getValue } = info;
	return (
		<Typography variant="tableCell" style={{ marginLeft: 16 }}>
			{getValue()}
		</Typography>
	);
}

function TextInputCell(info: CellContext<CustomMetadataColumn, string>) {
	const { row } = info;

	const autoSave = row.original.autoSave as UseTitleAutoSaveOutput<OptionalInherited<string>>;

	const onChange = (evt: ChangeEvent<HTMLInputElement>) => {
		autoSave.setValue((prev) => ({
			...prev,
			displayValue: evt.target.value === '' ? prev.inherited.value || '' : evt.target.value,
		}));
	};
	const isValueInherited = useMemo(
		() => (autoSave.value ? isInherited<string>({ inheritedObject: autoSave.value }).isInherited : false),
		[autoSave],
	);
	const onBlur = useCallback(() => {
		autoSave.commit();
	}, [autoSave]);

	return useMemo(
		() => (
			<CellWrapper>
				<TableTextInput
					aria-label={row.original.name}
					variant={autoSave.value?.displayValue === (autoSave.value as any)?.inherited?.value ? 'prefilled' : 'default'}
					placeholder={autoSave.value?.displayValue || ''}
					onBlur={onBlur}
					value={isValueInherited ? '' : autoSave.value?.displayValue}
					onChange={onChange}
				/>
			</CellWrapper>
		),
		[onBlur, autoSave.value, row.index],
	);
}

function getFormattedValue(value: string | null) {
	if (value === '' || value === null) return undefined;
	return value === 'true';
}

function BooleanCell({ row }: CellContext<CustomMetadataColumn, any>) {
	const autoSave = row.original.autoSave as UseTitleAutoSaveOutput<OptionalInherited<boolean>>;

	return (
		<CellWrapper>
			<DropdownV2
				ariaLabel={row.original.name}
				variant={autoSave.value?.displayValue === (autoSave.value as any)?.inherited?.value ? 'prefilled' : 'default'}
				value={autoSave.value?.displayValue?.toString() || ''}
				onChange={(e) => {
					autoSave.setValue((prev) => ({
						...prev,
						displayValue: getFormattedValue(e),
					}));
					autoSave.commit();
				}}
				transparentBorder
				backgroundColor="transparent"
				options={[
					{
						label: '',
						value: '',
					},
					{
						label: 'True',
						value: 'true',
					},
					{
						label: 'False',
						value: 'false',
					},
				]}
			/>
		</CellWrapper>
	);
}

function ValueCell(info: CellContext<CustomMetadataColumn, string | boolean | undefined>) {
	const { row } = info;
	if (row.original.type === 'string') {
		return TextInputCell(info as CellContext<CustomMetadataColumn, string>);
	}
	return BooleanCell(info as CellContext<CustomMetadataColumn, boolean | null>);
}

export default function CustomMetadataBase({ data }: CustomMetadataBaseProps) {
	const editMode = useTitleEditorStore(titleEditorStoreSelector.editMode);

	const columns: CustomColumnDef<CustomMetadataColumn>[] = useMemo(
		() => [
			{
				id: 'attribute',
				header: 'Attribute',
				accessorFn: (row) => row?.name,
				renderReadOnly: (v) => v,
				width: 200,
				cell: KeyCell,
			},
			{
				id: 'value',
				header: 'Value',
				accessorFn: (row: CustomMetadataColumn) => row,
				renderReadOnly: (v: CustomMetadataColumn) => {
					let displayValue = v.value;
					if (v.type === 'boolean') {
						if (v.value === 'true') displayValue = 'True';
						else if (v.value === 'false') displayValue = 'False';
					}

					return (
						<ScrollableReadOnlyWrapper>
							<InheritedWrapper>
								<ReadOnlyTypography
									variant={
										isInherited<string | boolean>({ inheritedObject: v.autoSave.value }).isInherited
											? 'prefilled'
											: 'default'
									}
								>
									{displayValue}
								</ReadOnlyTypography>
							</InheritedWrapper>
						</ScrollableReadOnlyWrapper>
					);
				},
				cell: ValueCell,
			},
		],
		[],
	);

	if (data.length === 0) return null;

	return (
		<div>
			<TitleEditableField label="Custom" direction="column" />
			<InheritanceOnValueChangeSimpleTableWrapper
				editMode={editMode}
				tableStyle="border"
				columns={columns}
				data={data}
			/>
		</div>
	);
}
